import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  PortalLoadingCard,
  PortalMetricCard,
  PortalStateCard,
  formatPortalDate,
  getStatusChipStyle
} from './portalShared'

const useStyles = makeStyles((theme) => ({
  heroCard: {
    borderRadius: 22,
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #163d31 0%, #215a47 60%, #9b7a3d 100%)',
    boxShadow: '0 18px 40px rgba(21, 71, 52, 0.18)'
  },
  heroContent: {
    padding: theme.spacing(3.5)
  },
  heroEyebrow: {
    color: 'rgba(233, 242, 236, 0.82)',
    letterSpacing: '0.12em'
  },
  heroTitle: {
    color: '#fff',
    fontWeight: 700,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5)
  },
  heroDescription: {
    color: 'rgba(241, 247, 243, 0.92)',
    maxWidth: 720
  },
  heroChip: {
    backgroundColor: 'rgba(255,255,255,0.16)',
    color: '#fff',
    fontWeight: 700
  },
  panelCard: {
    borderRadius: 20,
    boxShadow: '0 14px 30px rgba(31, 55, 42, 0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)',
    height: '100%'
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#20342b'
  },
  mutedText: {
    color: '#5d6c64'
  },
  scopeList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr'
    }
  },
  scopeItem: {
    padding: theme.spacing(1.75),
    borderRadius: 16,
    background: '#f7faf7',
    border: '1px solid #dce6df'
  },
  scopeLabel: {
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#6a7a72'
  },
  scopeValue: {
    marginTop: theme.spacing(0.75),
    fontWeight: 700,
    color: '#20342b'
  },
  statusWrap: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(2)
  },
  workCard: {
    borderRadius: 18,
    boxShadow: '0 12px 26px rgba(31, 55, 42, 0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)',
    marginBottom: theme.spacing(2)
  },
  workHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  workTitle: {
    fontWeight: 700,
    color: '#20342b'
  },
  infoRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  infoCard: {
    background: '#f8faf8',
    borderRadius: 14,
    border: '1px solid #dde6df',
    padding: theme.spacing(1.5)
  },
  infoLabel: {
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#6a7a72'
  },
  infoValue: {
    marginTop: theme.spacing(0.5),
    color: '#21352d'
  }
}))

const statusOrder = ['overdue', 'never_done', 'needs_completion', 'due_soon', 'ok', 'ignored']

const SubcontractorDashboard = ({ url, user }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [dashboard, setDashboard] = useState(null)

  const loadDashboard = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const response = await axios.get(`${url}/api/subcontractor/dashboard`)
      setDashboard(response.data || null)
    } catch (requestError) {
      console.error('Failed to load subcontractor dashboard', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load dashboard data right now.')
    } finally {
      setLoading(false)
    }
  }, [url])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  const profile = dashboard?.profile || user?.subcontractorProfile || {}
  const summary = dashboard?.summary || {}
  const scheduleSummary = summary.schedule || {}
  const nextUp = dashboard?.nextUp || []

  const nextScheduledDate = useMemo(() => {
    const datedItems = nextUp.filter(item => item?.nextScheduledDate)
    if (!datedItems.length) {
      return 'No upcoming date'
    }

    const sorted = [...datedItems].sort((a, b) => new Date(a.nextScheduledDate) - new Date(b.nextScheduledDate))
    return formatPortalDate(sorted[0].nextScheduledDate, { includeWeekday: true })
  }, [nextUp])

  if (loading) {
    return <PortalLoadingCard title="Loading dashboard" message="Pulling your subcontractor scope and upcoming herbicide work." minHeight={280} />
  }

  if (error) {
    return (
      <PortalStateCard
        title="Dashboard unavailable"
        message={error}
        actionLabel="Retry"
        onAction={loadDashboard}
        tone="danger"
        minHeight={280}
      />
    )
  }

  if (!dashboard) {
    return (
      <PortalStateCard
        title="No dashboard data"
        message="This account does not currently have any subcontractor dashboard data to display."
        actionLabel="Refresh"
        onAction={loadDashboard}
        minHeight={260}
      />
    )
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card className={classes.heroCard}>
          <CardContent className={classes.heroContent}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gridGap={12}>
              <div>
                <Typography variant="overline" className={classes.heroEyebrow}>
                  Daily Operating Snapshot
                </Typography>
                <Typography variant="h4" className={classes.heroTitle}>
                  {profile.vendorName || 'Subcontractor dashboard'}
                </Typography>
                <Typography variant="body1" className={classes.heroDescription}>
                  {profile.role === 'admin'
                    ? 'You have visibility across the subcontractor crews tied to this vendor. Use this dashboard to monitor upcoming work and active portal users.'
                    : 'Your portal is intentionally limited to your assigned crew, users in that scope, and the herbicide work routed through your territories.'}
                </Typography>
              </div>
              <Box display="flex" gridGap={8} flexWrap="wrap">
                <Chip label={(profile.role || 'applicator').toUpperCase()} className={classes.heroChip} />
                <Chip label={profile.crewName || 'All subcontractor crews'} className={classes.heroChip} />
                <Chip label={(profile.accessStatus || 'active').replace(/_/g, ' ')} className={classes.heroChip} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <PortalMetricCard
          eyebrow="Visible Users"
          value={summary.activeUsers || 0}
          caption="Employees currently in scope for this portal account."
          accent="green"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PortalMetricCard
          eyebrow="Crew Scope"
          value={summary.crews || 0}
          caption="Distinct crews represented in the visible subcontractor schedule."
          accent="gold"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PortalMetricCard
          eyebrow="Visible Jobs"
          value={scheduleSummary.total || 0}
          caption="Schedule items currently returned by the subcontractor API."
          accent="blue"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PortalMetricCard
          eyebrow="Next Date"
          value={nextScheduledDate}
          caption="Earliest upcoming scheduled work from the current dashboard feed."
          accent="plum"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Card className={classes.panelCard}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              Portal Scope
            </Typography>
            <Typography variant="body2" className={classes.mutedText}>
              These values come from the subcontractor access profile already attached to this login.
            </Typography>

            <Box mt={2.5} className={classes.scopeList}>
              <div className={classes.scopeItem}>
                <Typography className={classes.scopeLabel}>Signed In As</Typography>
                <Typography variant="body1" className={classes.scopeValue}>
                  {profile.displayName || user?.name || 'Unknown user'}
                </Typography>
              </div>
              <div className={classes.scopeItem}>
                <Typography className={classes.scopeLabel}>Vendor</Typography>
                <Typography variant="body1" className={classes.scopeValue}>
                  {profile.vendorName || 'Subcontractor'}
                </Typography>
              </div>
              <div className={classes.scopeItem}>
                <Typography className={classes.scopeLabel}>Crew</Typography>
                <Typography variant="body1" className={classes.scopeValue}>
                  {profile.crewName || 'All subcontractor crews'}
                </Typography>
              </div>
              <div className={classes.scopeItem}>
                <Typography className={classes.scopeLabel}>Default Landing</Typography>
                <Typography variant="body1" className={classes.scopeValue}>
                  {(profile.defaultLanding || 'subcontractor_dashboard').replace(/_/g, ' ')}
                </Typography>
              </div>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card className={classes.panelCard}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              Schedule Status Mix
            </Typography>
            <Typography variant="body2" className={classes.mutedText}>
              Status counts are based on the current subcontractor schedule filter used by the dashboard endpoint.
            </Typography>

            <div className={classes.statusWrap}>
              {statusOrder.map((status) => (
                <Chip
                  key={status}
                  label={`${status.replace(/_/g, ' ')}: ${scheduleSummary[status] || 0}`}
                  style={getStatusChipStyle(status)}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Next Up
        </Typography>
        <Box mt={2}>
          {!nextUp.length ? (
            <PortalStateCard
              title="No visible schedule items"
              message="The current subcontractor scope does not have any upcoming herbicide work yet."
              minHeight={220}
            />
          ) : (
            nextUp.map((item) => (
              <Card key={item._id} className={classes.workCard}>
                <CardContent>
                  <div className={classes.workHeader}>
                    <div>
                      <Typography variant="h6" className={classes.workTitle}>
                        {item.siteName || 'Unnamed property'}
                      </Typography>
                      <Typography variant="body2" className={classes.mutedText}>
                        {item.address || 'No address on file'}
                      </Typography>
                      <Typography variant="body2" className={classes.mutedText}>
                        {item.territoryName || 'Unassigned territory'}
                        {item.assignedApplicator ? ` • ${item.assignedApplicator}` : ''}
                      </Typography>
                    </div>

                    <Box display="flex" gridGap={8} flexWrap="wrap">
                      <Chip label={item.statusLabel || item.status} style={getStatusChipStyle(item.status)} />
                      {item.contractedForHerbicide ? (
                        <Chip label="Contracted" style={{ backgroundColor: '#edf4ff', color: '#23417e' }} />
                      ) : null}
                    </Box>
                  </div>

                  <div className={classes.infoRow}>
                    <div className={classes.infoCard}>
                      <Typography className={classes.infoLabel}>Next Scheduled Date</Typography>
                      <Typography variant="body2" className={classes.infoValue}>
                        {formatPortalDate(item.nextScheduledDate, { includeWeekday: true })}
                      </Typography>
                    </div>
                    <div className={classes.infoCard}>
                      <Typography className={classes.infoLabel}>Last Applied</Typography>
                      <Typography variant="body2" className={classes.infoValue}>
                        {formatPortalDate(item.lastAppliedDate, { fallback: 'No completed application recorded' })}
                      </Typography>
                    </div>
                    <div className={classes.infoCard}>
                      <Typography className={classes.infoLabel}>Gate Code</Typography>
                      <Typography variant="body2" className={classes.infoValue}>
                        {item.gateCode || 'Not provided'}
                      </Typography>
                    </div>
                    <div className={classes.infoCard}>
                      <Typography className={classes.infoLabel}>Crew Territory</Typography>
                      <Typography variant="body2" className={classes.infoValue}>
                        {item.territoryName || 'Unassigned territory'}
                      </Typography>
                    </div>
                  </div>

                  {item.notes ? (
                    <>
                      <Divider style={{ margin: '18px 0 14px' }} />
                      <Typography variant="body2" className={classes.mutedText}>
                        {item.notes}
                      </Typography>
                    </>
                  ) : null}
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(SubcontractorDashboard)
